import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output, ViewChild
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {RouterService} from '@app/core/services/router.service';
import {KontakteService} from '@app/core/state/kontakte';
import {FilterUtil} from '@app/core/util/filter.util';
import {StaticPathUtil} from '@app/core/util/static-path.util';
import {StringUtil} from '@app/core/util/string.util';
import {ContactDialogComponent, KontaktDialogData} from '@app/shared/dialog/contact-dialog/contact-dialog.component';
import {ListEntry} from '@app/shared/list/list.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Kontakt} from '@pezi/entities';
import {debounceTime, take} from 'rxjs/operators';
import {PeziNumericComponent} from "@app/shared/form/pezi-numeric/pezi-numeric.component";
import { PeziTextComponent } from '../form/pezi-text/pezi-text.component';
import { NgIf, NgStyle } from '@angular/common';
import { NoDataComponent } from '../no-data/no-data.component';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { MatListItemIcon } from '@angular/material/list';
import { LoaderComponent } from '../loader/loader.component';
import { PeziButtonComponent } from '../pezi-button/pezi-button.component';

@UntilDestroy()
@Component({
  selector:        'app-contact-search',
  templateUrl:     './contact-search.component.html',
  styleUrls:       ['./contact-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:      true,
  imports:         [
    ReactiveFormsModule,
    PeziTextComponent,
    NgIf,
    NoDataComponent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    MatListItemIcon,
    NgStyle,
    LoaderComponent,
    PeziButtonComponent
  ]
})
export class ContactSearchComponent implements OnInit, AfterViewInit {

  @ViewChild('filter', {static: false}) filterField: PeziNumericComponent;

  public kontaktSearch = new FormGroup({
    search: new FormControl(
      {
        value: null,
        disabled: true
      }
    )
  });

  public alreadySearchedOnce = false;

  originalKontakte: ListEntry<Kontakt>[];
  filteredKontakte: ListEntry<Kontakt>[] = [];

  @Input()
  public disableDialog = false;

  @Output()
  public onContactSelected = new EventEmitter<ListEntry<Kontakt>>();

  @Output()
  public onOverviewButtonClicked = new EventEmitter();

  public kontakteLoading = false;

  constructor(private routerService: RouterService, private kontakteService: KontakteService, private staticPath: StaticPathUtil,
              private cd: ChangeDetectorRef, private dialog: MatDialog) {
  }

  ngOnInit() {
    this.kontaktSearch.valueChanges.pipe(untilDestroyed(this),
      debounceTime(150))
      .subscribe(val => {
        if (val) {
          this.alreadySearchedOnce = true;
          this.displaySuggestions(val.search);
        }
      });
    this.initKontakte();
  }

  ngAfterViewInit() {
    if (this.filterField) {
      this.filterField.focusText();
    }
  }

  public openContactSearch(): void {
    this.onOverviewButtonClicked.emit();
    this.routerService.navigateContactOverviewWithSearch(this.kontaktSearch.controls.search.value);
  }

  public selectContact(entry: ListEntry<Kontakt>): void {
    this.onContactSelected.emit(entry);
    if (!this.disableDialog) {
      this.dialog.open(ContactDialogComponent, {
        data: {kontakt: entry.data} as KontaktDialogData
      });
    }
  }

  private initKontakte(): void {
    if (!this.kontakteService.canLoadKontakteFromCache()) {
      this.kontakteLoading = true; // spinner nur anzeigen wenn keine Kontakte im cache
      this.cd.markForCheck();
    }
    this.kontakteService.getKontakte()
      .pipe(
        take(1),
        untilDestroyed(this))
      .subscribe(v => {
        this.kontaktSearch.controls.search.enable({emitEvent: false});
        this.kontakteLoading = false;
        this.originalKontakte = v.map<ListEntry<Kontakt>>((kontakt: Kontakt) => ({
          text: StringUtil.getPersonDescWithTitle(kontakt),
          imageURL: this.staticPath.getPersonImgPath() + kontakt.image,
          subtext: StringUtil.getKontaktSubText(kontakt),
          additionalText: kontakt.abteilung,
          fallbackURL: this.staticPath.getFallbackPath(),
          data: kontakt
        }));
        this.cd.markForCheck();
        if (this.filterField) {
          this.filterField.focusText();
        }
      });
  }

  private displaySuggestions(searchText: string): void {
    this.filteredKontakte = [];
    this.filteredKontakte = FilterUtil.filterKontakte(searchText, this.originalKontakte);
    this.cd.markForCheck();
  }

}
